import React, { useMemo, useCallback, useState } from 'react';
import { Modal } from '../shared';
import { useStore } from '../../store';
import { SUPER_REGION_OPTIONS } from '../../constants/regions';
import type { Profile, Application, AppPoolCoverageEntry } from '@interlucent/organization-protocol';
import { send } from '../../ws';
import { getWs } from '../../wsConnection';
import { waitForAck } from '../../utils/waitForAck';
import { correlationId } from '@interlucent/ids';
import styles from './OnDemandAvailability.module.css';

interface OnDemandAvailabilityProps {
  projectId: string;
}

const REGIONS = SUPER_REGION_OPTIONS.map(r => r.value);
const REGION_SHORT_LABELS: Record<string, string> = {
  wnam: 'WNAM',
  enam: 'ENAM',
  weur: 'WEUR',
  eeur: 'EEUR',
  apac: 'APAC',
  oc: 'OC',
  latam: 'LATAM',
};

// Draft state: appId -> set of regions
type DraftState = Record<string, Set<string>>;

function buildDraftFromLive(
  coverage: Record<string, AppPoolCoverageEntry[]>,
  allProfiles: Record<string, Profile>,
): DraftState {
  const draft: DraftState = {};
  for (const [appId, entries] of Object.entries(coverage)) {
    for (const entry of entries) {
      const profile = allProfiles[entry.poolId];
      if (!profile || profile.type !== 'by_request') continue;
      if (!draft[appId]) draft[appId] = new Set();
      draft[appId].add(profile.region);
    }
  }
  return draft;
}

export function OnDemandAvailability({ projectId }: OnDemandAvailabilityProps) {
  const applications = useStore(s => s.applications);
  const allProfiles = useStore(s => s.profiles);
  const appPoolCoverage = useStore(s => s.appPoolCoverage);
  const isConnected = useStore(s => s.connectionState === 'connected');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [draft, setDraft] = useState<DraftState | null>(null);

  const projectApps = useMemo(() => {
    return Object.values(applications).filter((app: Application) => app.projectId === projectId);
  }, [applications, projectId]);

  const onDemandProfiles = useMemo(() => {
    return Object.values(allProfiles).filter(
      (p: Profile) => p.type === 'by_request' && !p.deletedAt
    );
  }, [allProfiles]);

  const regionProfileMap = useMemo(() => {
    const map: Record<string, Profile> = {};
    for (const profile of onDemandProfiles) {
      map[profile.region] = profile;
    }
    return map;
  }, [onDemandProfiles]);

  // Live state from server — derived from materialized pool coverage, so a
  // checkbox is checked only when the app is actually provisioned in that
  // region's on-demand pool.
  const liveState = useMemo(() => {
    return buildDraftFromLive(appPoolCoverage, allProfiles);
  }, [appPoolCoverage, allProfiles]);

  // Current working state: draft if editing, else live
  const currentState = draft ?? liveState;

  const isEditing = draft !== null;

  const isChecked = useCallback((appId: string, region: string) => {
    return currentState[appId]?.has(region) ?? false;
  }, [currentState]);

  // Only consider regions that have a global pool
  const availableRegions = useMemo(() => {
    return REGIONS.filter(r => regionProfileMap[r]);
  }, [regionProfileMap]);

  const isAllChecked = useCallback((appId: string) => {
    return availableRegions.length > 0 && availableRegions.every(r => currentState[appId]?.has(r));
  }, [currentState, availableRegions]);

  const isSomeChecked = useCallback((appId: string) => {
    return availableRegions.some(r => currentState[appId]?.has(r));
  }, [currentState, availableRegions]);

  // Start editing: copy live state into draft
  const ensureDraft = useCallback(() => {
    if (!draft) {
      // Deep copy the live state
      const copy: DraftState = {};
      for (const [appId, regions] of Object.entries(liveState)) {
        copy[appId] = new Set(regions);
      }
      setDraft(copy);
    }
  }, [draft, liveState]);

  const toggleRegion = useCallback((appId: string, region: string) => {
    ensureDraft();
    setDraft(prev => {
      const state = prev ?? {};
      const copy = { ...state };
      if (!copy[appId]) copy[appId] = new Set();
      else copy[appId] = new Set(copy[appId]);

      if (copy[appId].has(region)) {
        copy[appId].delete(region);
      } else {
        copy[appId].add(region);
      }
      return copy;
    });
  }, [ensureDraft]);

  const toggleGlobal = useCallback((appId: string) => {
    ensureDraft();
    setDraft(prev => {
      const state = prev ?? {};
      const copy = { ...state };
      const allEnabled = availableRegions.every(r => copy[appId]?.has(r));
      if (allEnabled) {
        copy[appId] = new Set();
      } else {
        copy[appId] = new Set(availableRegions);
      }
      return copy;
    });
  }, [ensureDraft, availableRegions]);

  const discardDraft = useCallback(() => {
    setDraft(null);
  }, []);

  // Compute diff between live and draft
  const computeChanges = useCallback(() => {
    if (!draft) return { toAdd: [], toRemove: [] };
    const toAdd: Array<{ appId: string; appName: string; region: string }> = [];
    const toRemove: Array<{ appId: string; appName: string; region: string }> = [];

    const allAppIds = new Set([...Object.keys(liveState), ...Object.keys(draft)]);
    for (const appId of allAppIds) {
      const liveRegions = liveState[appId] ?? new Set();
      const draftRegions = draft[appId] ?? new Set();
      const app = applications[appId];
      const appName = app?.name ?? appId;

      for (const r of REGIONS) {
        if (draftRegions.has(r) && !liveRegions.has(r)) {
          toAdd.push({ appId, appName, region: r });
        }
        if (!draftRegions.has(r) && liveRegions.has(r)) {
          toRemove.push({ appId, appName, region: r });
        }
      }
    }
    return { toAdd, toRemove };
  }, [draft, liveState, applications]);

  const hasChanges = useMemo(() => {
    const { toAdd, toRemove } = computeChanges();
    return toAdd.length > 0 || toRemove.length > 0;
  }, [computeChanges]);

  // Apply changes
  const applyChanges = useCallback(async () => {
    if (!getWs() || !draft) return;
    setSaving(true);
    setConfirmOpen(false);

    try {
      const { toAdd, toRemove } = computeChanges();

      // Process removals first
      for (const { appId, region } of toRemove) {
        const profile = regionProfileMap[region];
        if (!profile) continue;
        const corr = correlationId();
        send('mapping.unmap', { profileId: profile.id, appId }, corr);
        await waitForAck(corr);
      }

      // Process additions (only for regions with an existing global pool)
      for (const { appId, region } of toAdd) {
        const profileId = regionProfileMap[region]?.id;
        if (!profileId) continue;
        const corr = correlationId();
        send('mapping.map', { profileId, appId }, corr);
        await waitForAck(corr);
      }

      setDraft(null);
    } finally {
      setSaving(false);
    }
  }, [draft, computeChanges, regionProfileMap, mappings]);

  const changes = computeChanges();

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        Configure which super regions each application is available in via on-demand shared pools.
        Select "Global" to enable all regions.
      </p>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Application</th>
            <th>Global</th>
            {REGIONS.map(r => (
              <th key={r}>{REGION_SHORT_LABELS[r]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {projectApps.length === 0 ? (
            <tr>
              <td colSpan={2 + REGIONS.length} className={styles.emptyRow}>
                No applications in this project yet.
              </td>
            </tr>
          ) : (
            projectApps.map(app => (
              <tr key={app.id}>
                <td>
                  <span className={styles.appName}>{app.name}</span>
                </td>
                <td>
                  <label className={styles.checkLabel}>
                    <input
                      type="checkbox"
                      ref={el => {
                        if (el) el.indeterminate = isSomeChecked(app.id) && !isAllChecked(app.id);
                      }}
                      checked={isAllChecked(app.id)}
                      onChange={() => toggleGlobal(app.id)}
                      disabled={saving || !isConnected}
                    />
                  </label>
                </td>
                {REGIONS.map(region => (
                  <td key={region}>
                    <label className={styles.checkLabel}>
                      <input
                        type="checkbox"
                        checked={isChecked(app.id, region)}
                        onChange={() => toggleRegion(app.id, region)}
                        disabled={saving || !isConnected || !regionProfileMap[region]}
                      />
                    </label>
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {isEditing && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.discardButton}
            onClick={discardDraft}
            disabled={saving}
          >
            Discard
          </button>
          <button
            type="button"
            className={styles.applyButton}
            onClick={() => setConfirmOpen(true)}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Applying...' : 'Apply Changes'}
          </button>
        </div>
      )}

      {confirmOpen && (
        <Modal
          isOpen={true}
          title="Confirm On-Demand Changes"
          subtitle="These changes will provision or deprovision shared pool resources."
          onClose={() => setConfirmOpen(false)}
          size="medium"
        >
          <div className={styles.confirmContent}>
            {changes.toAdd.length > 0 && (
              <div className={styles.changeGroup}>
                <h4 className={styles.changeLabel}>Enable</h4>
                <ul className={styles.changeList}>
                  {changes.toAdd.map(c => (
                    <li key={`${c.appId}-${c.region}`}>
                      <strong>{c.appName}</strong> in {REGION_SHORT_LABELS[c.region]}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {changes.toRemove.length > 0 && (
              <div className={styles.changeGroup}>
                <h4 className={styles.changeLabel}>Disable</h4>
                <ul className={styles.changeList}>
                  {changes.toRemove.map(c => (
                    <li key={`${c.appId}-${c.region}`}>
                      <strong>{c.appName}</strong> in {REGION_SHORT_LABELS[c.region]}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={applyChanges}
              >
                Confirm
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
